import React, { ReactNode, useState } from 'react';
import { styles } from './CartLineItem.css';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import DeleteIcon from '@jbhi-fi/lanyard-ui/components/Icons/Delete';
import { formatMoney } from '@jbhi-fi/lanyard-ui/global/utils';
import { QuantitySelect } from '../../QuantitySelect/QuantitySelect';
import { Tag } from '@jbhi-fi/lanyard-ui/components/Icons';
import { BundleBuyBanner } from '../BundleOffering/BundleBuyBanner/BundleBuyBanner';
import Spinner from '@jbhi-fi/lanyard-ui/components/Spinner';
import { GiftCardDetails, GiftCardDetailsProps } from '../GiftCardDetails/GiftCardDetails';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';

export interface CartLineItemProps {
  title: string;
  imageUrl: string;
  itemUrl: string;
  price: number;
  quantity: number;
  maxQuantity?: number;
  wasPrice?: number;
  discounts?: Array<string>;
  marketplaceSeller?: string;
  giftCardDetails?: GiftCardDetailsProps;
  topAlerts?: ReactNode;
  bottomAlerts?: ReactNode;
  serviceOfferings?: ReactNode;
  attachedServices?: ReactNode;
  bundleOfferings?: ReactNode;
  wishlistCard?: ReactNode;
  isFlipped?: boolean;
  onRemove?: () => Promise<void> | void;
  onQuantityChange?: (quantity: number) => Promise<void> | void;
  variant?: 'cart' | 'miniCart';
  isNoLongerAvailable?: boolean;
}

export const CartLineItem = ({
  title,
  itemUrl,
  wasPrice,
  price,
  quantity,
  maxQuantity = 5,
  imageUrl,
  marketplaceSeller,
  giftCardDetails,
  discounts,
  topAlerts,
  bottomAlerts,
  serviceOfferings,
  attachedServices,
  bundleOfferings,
  wishlistCard,
  onRemove,
  onQuantityChange,
  variant = 'cart',
  isNoLongerAvailable,
  isFlipped = false,
}: CartLineItemProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const isDiscounted = !!wasPrice && wasPrice > price;
  const isMobileView = useBreakpoint('md', 'down') || variant === 'miniCart';

  const handleQuantityChange = async (qty: number) => {
    if (!onQuantityChange || isLoading) return;
    setIsLoading(true);
    try {
      await onQuantityChange(qty);
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  const handleRemove = async () => {
    if (!onRemove || isLoading) return;
    setIsLoading(true);
    try {
      await onRemove();
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.container({ isFlipped: isFlipped && !!wishlistCard, variant })}>
        {topAlerts ? <div>{topAlerts}</div> : null}
        <div className={styles.mainSection}>
          <a className={styles.link} href={itemUrl}>
            <img src={imageUrl} className={styles.image} alt={title} />
          </a>
          <div className={styles.rightSection}>
            <div className={styles.topRow}>
              <div className={styles.productDetails} data-testid={`${variant}-product-title`}>
                <a className={styles.link} href={itemUrl}>
                  {title}
                </a>
                {marketplaceSeller ? (
                  <div className={styles.marketplaceSeller}>
                    <b>JB Hi-Fi Marketplace:</b> Sold and sent by {marketplaceSeller}
                  </div>
                ) : null}
                {discounts?.map(discount => (
                  <div key={discount} className={styles.discount}>
                    <Tag />
                    {discount}
                  </div>
                ))}
                {bottomAlerts ? <div>{bottomAlerts}</div> : null}
                {giftCardDetails ? <GiftCardDetails {...giftCardDetails} /> : null}
              </div>
              <div className={styles.actions}>
                {!isNoLongerAvailable ? (
                  <div>
                    {onQuantityChange && maxQuantity > 1 ? (
                      <QuantitySelect
                        quantity={quantity}
                        maxQty={maxQuantity}
                        onChange={handleQuantityChange}
                        variant="small"
                        disabled={isLoading}
                      />
                    ) : null}
                  </div>
                ) : null}

                <div className={styles.priceRemoveBlock}>
                  {!isNoLongerAvailable ? (
                    <div className={styles.price}>
                      {isDiscounted ? <s>{formatMoney(wasPrice)}</s> : null}
                      <div className={styles.finalPrice({ isDiscounted })} data-testid={`${variant}-price`}>
                        {formatMoney(price)}
                      </div>
                    </div>
                  ) : null}

                  {onRemove ? (
                    isLoading ? (
                      <div className={styles.loading}>
                        <Spinner width={22} height={22} />
                      </div>
                    ) : (
                      <Button
                        type="linkWithIcon"
                        icon={<DeleteIcon />}
                        iconPosition="left"
                        onClick={handleRemove}
                        label="Remove"
                        data-testid={`${variant}-remove`}
                      />
                    )
                  ) : null}
                </div>
              </div>
            </div>
            {attachedServices ? <div className={styles.attachedServices}>{attachedServices}</div> : null}
          </div>
        </div>
        {serviceOfferings && isMobileView ? <b>Do you need?</b> : null}
        {serviceOfferings ? (
          <div className={styles.serviceOfferings}>
            {!isMobileView ? <b>Do you need?</b> : null}
            {serviceOfferings}
          </div>
        ) : null}
        {bundleOfferings ? (
          <div className={styles.bundleOfferings}>
            <BundleBuyBanner />
            {bundleOfferings}
          </div>
        ) : null}
      </div>
      {wishlistCard ? (
        <div className={styles.container({ isFlipped: !isFlipped, isBackface: true })}>{wishlistCard}</div>
      ) : null}
    </div>
  );
};
